import { ApolloError } from "@apollo/client";
import { ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteLoader from "react-window-infinite-loader";

import ListEntitiesNew from "components/ListEntitiesNew";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import useErrorHandle from "hooks/useErrorHandle";
import useCanManageVCSIntegration from "shared/VCS/access/useCanManageVCSIntegration";
import { VcsIntegration, VcsProvider } from "types/generated";

import { showVCSIntegrationDetailsDrawer } from "../VcsShared/components/Details";
import { getEditIntegrationURL } from "../VcsShared/helpers";
import { VCSIntegrationsListContextProvider } from "./Context";
import VCSIntegrationsEmpty from "./Empty";
import VCSIntegrationsFiltersLayout from "./FiltersLayout";
import VCSIntegrationsHasNoResults from "./HasNoResults";
import VCSIntegrationsVirtualizedListItem from "./Item/Virtualized";
import VCSIntegrationsPageLayout from "./PageLayout";
import { ITEMS_LIMIT } from "./constants";
import { getVCSIntegrationId } from "./helpers";

export type VcsListProps = {
  provider: VcsProvider;
  builtInIntegration?: ReactNode;
  integrations: VcsIntegration[];
  isPageEmpty: boolean;
  isPageLoading: boolean;
  isPageNotFound: boolean;
  hasNextPage: boolean;
  error: ApolloError | undefined;
  loadMoreItems: () => void;
  refetchIntegrations: () => void;
  hasNoResults: boolean;
};

const VCSIntegrationsList = ({
  provider,
  builtInIntegration,
  integrations,
  isPageEmpty,
  isPageLoading,
  isPageNotFound,
  hasNextPage,
  error,
  loadMoreItems,
  refetchIntegrations,
  hasNoResults,
}: VcsListProps) => {
  const navigate = useNavigate();

  const isItemLoaded = (value: number) => !hasNextPage || value < integrations.length;
  const canManageVCSIntegration = useCanManageVCSIntegration();

  const handleOpenDetailsDrawer = (integration: VcsIntegration) => {
    const canManageFocusedVCSIntegration = canManageVCSIntegration(integration);

    showVCSIntegrationDetailsDrawer({
      id: getVCSIntegrationId(integration),
      item: integration,
      canEdit: !!canManageFocusedVCSIntegration,
    });
  };

  const handleIntegrationEdit = useCallback(
    (item: VcsIntegration) => {
      if (item.id) {
        navigate(getEditIntegrationURL(item.provider, item.id));
      }
    },
    [navigate]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (isPageNotFound) {
    return <NotFoundPage />;
  }

  return (
    <VCSIntegrationsListContextProvider>
      <VCSIntegrationsPageLayout>
        <Box role="table" direction="column" grow={!isPageEmpty && !hasNoResults ? "1" : undefined}>
          {(!isPageEmpty || builtInIntegration) && <VCSIntegrationsFiltersLayout />}
          {builtInIntegration}

          {!isPageEmpty && !hasNoResults && (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={hasNextPage ? integrations.length + ITEMS_LIMIT : integrations.length}
              loadMoreItems={loadMoreItems}
            >
              {({ onItemsRendered }) => (
                <ListEntitiesNew
                  itemCount={integrations.length}
                  itemProps={{
                    items: integrations,
                    onEdit: handleIntegrationEdit,
                    onShowDetails: handleOpenDetailsDrawer,
                    onIntegrationDelete: refetchIntegrations,
                  }}
                  virtualizedItem={VCSIntegrationsVirtualizedListItem}
                  itemKey={(index) => getVCSIntegrationId(integrations[index])}
                  onItemsRendered={onItemsRendered}
                />
              )}
            </InfiniteLoader>
          )}
        </Box>
        {isPageEmpty && <VCSIntegrationsEmpty provider={provider} />}
        {hasNoResults && <VCSIntegrationsHasNoResults />}
      </VCSIntegrationsPageLayout>
    </VCSIntegrationsListContextProvider>
  );
};

export default VCSIntegrationsList;
